import React from 'react';
import emojiRegex from 'emoji-regex';
import useResizeObserver from 'enhancers/use-resize-observer';
import type { StyledElement, AriaAttributes, TestAutomation, RenderIconOrElement } from 'contracts';
import toArray from 'utils/to-array';
import is from 'utils/is';

import HelperText from '../form/helper-text';
import Html from '../html';
import Loading from '../loading';
import { IconList } from '../icon';
import renderElement from '../utils/render-element';

import styles from './input.module.scss';

/**
 * @see {https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#%3Cinput%3E_types}
 */
type InputType =
  | 'color'
  | 'date'
  | 'datetime-local'
  | 'email'
  | 'month'
  | 'number'
  | 'password'
  | 'search'
  | 'tel'
  | 'text'
  | 'time'
  | 'url'
  | 'week';

type HTMLInput = React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;

type KeyboardType = React.HTMLAttributes<HTMLInputElement>['inputMode'];

interface InputElement
  extends StyledElement,
    AriaAttributes,
    TestAutomation,
    Pick<HTMLInput, 'autoComplete' | 'maxLength' | 'minLength' | 'placeholder' | 'min' | 'max' | 'step'> {
  /**
   * @see {https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#%3Cinput%3E_types}
   */
  type?: InputType;
  name?: string;
  value: string | undefined;
  loading?: boolean;
  error?: boolean;
  pattern?: RegExp;
  prefix?: RenderIconOrElement;
  suffix?: RenderIconOrElement;
  highlight?: string | Array<string>;
  keyboardType?: KeyboardType;
  readOnly?: boolean;
  disabled?: boolean;
  preventClipboardPaste?: boolean;
  preventEmojis?: boolean;
  preventInvalidChanges?: boolean;
  variant?: 'standard' | 'outline';
  label?: string;
  helperText?: string | React.ReactNode;
  spaceless?: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
}

const basePadding = 10;
const emojiRegExp = emojiRegex();

const Input: React.FunctionComponent<InputElement> = (props) => {
  const {
    name,
    autoComplete,
    className,
    loading,
    error,
    maxLength,
    minLength,
    onChange,
    onFocus,
    onBlur,
    onKeyDown,
    placeholder,
    prefix,
    pattern,
    style,
    suffix,
    testId,
    type = 'text',
    highlight,
    keyboardType,
    readOnly,
    disabled,
    min,
    max,
    step,
    value: defaultValue = '',
    preventClipboardPaste = false,
    preventEmojis,
    preventInvalidChanges,
    variant = 'standard',
    label,
    helperText,
    spaceless,
    ...rest
  } = props;
  const [value, setValue] = React.useState<string | undefined>(defaultValue);
  const isOutlineVariant = variant === 'outline';

  const localRefs = {
    prefix: React.useRef<HTMLSpanElement>(undefined),
    suffix: React.useRef<HTMLSpanElement>(undefined),
    input: React.useRef<HTMLInputElement>(undefined),
    highlightRef: React.useRef<HTMLSpanElement>(undefined),
  };

  const computePadding = (element: React.MutableRefObject<HTMLSpanElement>): number | undefined => {
    if (is.nullish(element.current)) return undefined;

    return element.current.clientWidth + basePadding * 2;
  };

  const paddingLeft = useResizeObserver(localRefs.prefix, computePadding);
  const paddingRight = useResizeObserver(localRefs.suffix, computePadding);

  const textHighlights = React.useMemo(() => {
    const list = toArray(highlight)
      .filter(Boolean)
      .map((word) => word!.toLowerCase());

    if (!value || !list.length || list.every((word) => !value.toLowerCase().includes(word))) return;

    return value
      .split(new RegExp(`(${list.join('|')})`, 'i'))
      .filter(Boolean)
      .map((word) => {
        if (list.includes(word.toLowerCase())) {
          return (
            <Html.span key={word} className={[styles.highlight]}>
              {word}
            </Html.span>
          );
        }

        return word;
      });
  }, [value, highlight]);

  React.useEffect(() => {
    setValue(defaultValue ?? '');
  }, [defaultValue]);

  React.useLayoutEffect(() => {
    if (!textHighlights || !localRefs.highlightRef.current || !localRefs.input.current) {
      return;
    }

    const inputStyles = window.getComputedStyle(localRefs.input.current);

    localRefs.highlightRef.current.style.padding = inputStyles.padding;
    localRefs.highlightRef.current.style.paddingLeft = inputStyles.paddingLeft;
    localRefs.highlightRef.current.style.paddingRight = inputStyles.paddingRight;
    localRefs.highlightRef.current.style.borderColor = 'transparent';

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textHighlights]);

  React.useEffect(() => {
    const preventDefault: EventListenerOrEventListenerObject = (event): void => event.preventDefault();

    if (preventClipboardPaste) {
      localRefs.input.current?.addEventListener('paste', preventDefault);
    }

    return () => {
      if (preventClipboardPaste) {
        localRefs.input.current?.removeEventListener('paste', preventDefault);
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      if (preventInvalidChanges && pattern && Boolean(event.target.value.length) && !pattern.test(event.target.value)) {
        event.preventDefault();

        return;
      }

      setValue(event.target.value);

      if (is.func(onChange)) {
        onChange(event);
      }
    },
    [onChange, pattern, preventInvalidChanges]
  );

  const handleOnFocus = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      if (is.func(onFocus)) {
        onFocus(event);
      }
    },
    [onFocus]
  );

  const handleOnBlur = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      if (is.func(onBlur)) {
        onBlur(event);
      }
    },
    [onBlur]
  );

  const handleOnBeforeInput = React.useCallback(
    (event: React.FormEvent<HTMLInputElement> & { data?: string }) => {
      if (preventEmojis && is.string(event.data) && !is.nullish(event.data.match(emojiRegExp))) {
        event.preventDefault();
      }

      if (!pattern) return;

      if (!pattern.test(event.data ?? '')) {
        event.preventDefault();
      }
    },
    [pattern, preventEmojis]
  );

  const handleOnKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (disabled) {
        event.preventDefault();

        return;
      }

      if (is.func(onKeyDown)) {
        onKeyDown(event);
      }
    },
    [onKeyDown, disabled]
  );

  const prefixElement = React.useMemo(() => {
    if (!prefix) {
      return;
    }

    return renderElement(prefix);
  }, [prefix]);

  const suffixElement = React.useMemo(() => {
    if (![error, suffix, loading].some(Boolean)) {
      return;
    }

    if (loading) {
      return renderElement(
        <Loading testId={testId && `${testId}-loading`} delay={0} size={(isOutlineVariant && 16) || 20} thickness={2} />
      );
    }

    if (suffix) {
      return renderElement(suffix);
    }

    if (error) {
      return renderElement(IconList.warningCircularSolid);
    }
  }, [suffix, error, loading, testId, isOutlineVariant]);

  return (
    <Html.span
      className={[
        styles.wrapper,
        disabled && styles.disabled,
        isOutlineVariant && styles.outline,
        ...toArray(className),
      ]}
      style={style}
    >
      <Html.span className="w-100">
        {textHighlights && (
          <Html.span
            className={[styles.highlightContainer]}
            style={{
              paddingLeft,
              paddingRight,
            }}
          >
            <Html.span
              testId={testId && `${testId}-text-highlights`}
              className={[styles.input, error && styles.error, styles.highlightWrapper]}
              ref={localRefs.highlightRef}
            >
              {textHighlights}
            </Html.span>
          </Html.span>
        )}
        <Html.input
          ref={localRefs.input}
          type={type}
          testId={testId}
          name={name}
          placeholder={(isOutlineVariant && label && ' ') || placeholder}
          autoComplete={autoComplete}
          minLength={minLength}
          maxLength={maxLength}
          min={min}
          max={max}
          step={step}
          onBeforeInput={handleOnBeforeInput}
          onChange={handleOnChange}
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
          onKeyDown={handleOnKeyDown}
          value={value}
          inputMode={keyboardType}
          readOnly={readOnly}
          disabled={disabled}
          className={[
            styles.input,
            error && styles.error,
            isOutlineVariant && label && styles.hasLabel,
            'w-100',
            ...toArray(className),
          ]}
          style={{
            paddingLeft,
            paddingRight,
            ...(style || {}),
          }}
          arias={rest}
        />
        {isOutlineVariant && label && (
          <React.Fragment>
            <Html.label className={[styles.label, prefix && styles.labelWithPrefixSpacing]}>{label}</Html.label>
            <Html.fieldset className={styles.fieldset}>
              <Html.legend className={styles.legend}>
                <Html.div className={styles.legendText}>{label}</Html.div>
              </Html.legend>
            </Html.fieldset>
          </React.Fragment>
        )}
      </Html.span>
      {prefixElement && (
        <Html.span testId={testId && `${testId}-prefix`} className={styles.prefix} ref={localRefs.prefix}>
          {prefixElement}
        </Html.span>
      )}
      {suffixElement && (
        <Html.span
          testId={testId && `${testId}-suffix`}
          className={[styles.suffix, error && styles.error]}
          ref={localRefs.suffix}
        >
          {suffixElement}
        </Html.span>
      )}
      {isOutlineVariant && (helperText || !spaceless) && (
        <Html.div className={[styles.footer, spaceless && styles.minimized]}>
          {helperText && (
            <HelperText error={error} testId={testId && `${testId}-helper-text`}>
              {helperText}
            </HelperText>
          )}
        </Html.div>
      )}
    </Html.span>
  );
};

export type { InputElement, InputType };
export default React.memo(Input);
